import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { CognitoUserPool, CognitoUser, AuthenticationDetails, CognitoUserAttribute, CognitoUserSession } from 'amazon-cognito-identity-js';
import { loadConfig, type AppConfig } from '../lib/config';

interface AuthCtx {
  ready: boolean;
  config: AppConfig | null;
  user: { email: string; sub: string } | null;
  idToken: string | null;
  approved: boolean | null;
  signUp: (email: string, password: string, zip: string) => Promise<{ pending: boolean; email: string }>;
  confirm: (email: string, code: string) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
  oauthSignIn: (provider: 'Google' | 'SignInWithApple') => void;
  refreshSession: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<{ email: string; sub: string } | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [approved, setApproved] = useState<boolean | null>(null);

  const pool = useMemo(() => {
    if (!config) return null;
    return new CognitoUserPool({ UserPoolId: config.userPoolId, ClientId: config.userPoolClientId });
  }, [config]);

  useEffect(() => { loadConfig().then(setConfig).catch(console.error); }, []);

  useEffect(() => {
    if (!pool) return;
    const current = pool.getCurrentUser();
    if (!current) { setReady(true); return; }
    current.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) { setReady(true); return; }
      const payload = session.getIdToken().payload as Record<string, string>;
      setUser({ email: payload.email, sub: payload.sub });
      setIdToken(session.getIdToken().getJwtToken());
      setApproved(payload['custom:approved'] === 'true');
      setReady(true);
    });
    // Handle Cognito Hosted UI redirect back with ?code=
    handleOAuthRedirect();
  }, [pool]);

  async function handleOAuthRedirect() {
    if (!config) return;
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    if (!code || !window.location.pathname.startsWith('/auth/callback')) return;
    const redirectUri = `${window.location.origin}/auth/callback`;
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.userPoolClientId,
      code,
      redirect_uri: redirectUri,
    });
    const res = await fetch(`${config.hostedUiDomain}/oauth2/token`, {
      method: 'POST',
      headers: { 'content-type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!res.ok) {
      window.location.replace('/?error=oauth_failed');
      return;
    }
    const tokens = await res.json() as { id_token: string; access_token: string; refresh_token: string };
    // amazon-cognito-identity-js doesn't seamlessly accept Hosted UI tokens; we stash them and use them directly.
    sessionStorage.setItem('gg.idToken', tokens.id_token);
    setIdToken(tokens.id_token);
    const payload = JSON.parse(atob(tokens.id_token.split('.')[1])) as Record<string, string>;
    setUser({ email: payload.email, sub: payload.sub });
    setApproved(payload['custom:approved'] === 'true');
    window.history.replaceState({}, '', '/');
  }

  async function signUp(email: string, password: string, zip: string) {
    if (!pool) throw new Error('not ready');
    // Stash zip server-side before creating the account so cognito pre-signup doesn't lose it.
    const cfg = config!;
    await fetch(`${cfg.apiBase}/public/signup-meta`, {
      method: 'POST', headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ email, zip }),
    });
    return new Promise<{ pending: boolean; email: string }>((resolve, reject) => {
      const attrs = [
        new CognitoUserAttribute({ Name: 'email', Value: email }),
        new CognitoUserAttribute({ Name: 'custom:zip', Value: zip }),
        new CognitoUserAttribute({ Name: 'custom:approved', Value: 'false' }),
        new CognitoUserAttribute({ Name: 'custom:role', Value: 'user' }),
        new CognitoUserAttribute({ Name: 'custom:radius', Value: '50' }),
      ];
      pool.signUp(email, password, attrs, [], (err) => {
        if (err) reject(err);
        else resolve({ pending: true, email });
      });
    });
  }

  async function confirm(email: string, code: string) {
    if (!pool) throw new Error('not ready');
    const u = new CognitoUser({ Username: email, Pool: pool });
    await new Promise<void>((resolve, reject) => {
      u.confirmRegistration(code, true, (err) => err ? reject(err) : resolve());
    });
  }

  async function signIn(email: string, password: string) {
    if (!pool) throw new Error('not ready');
    const u = new CognitoUser({ Username: email, Pool: pool });
    const details = new AuthenticationDetails({ Username: email, Password: password });
    await new Promise<void>((resolve, reject) => {
      u.authenticateUser(details, {
        onSuccess: (session) => {
          const payload = session.getIdToken().payload as Record<string, string>;
          setUser({ email: payload.email, sub: payload.sub });
          setIdToken(session.getIdToken().getJwtToken());
          setApproved(payload['custom:approved'] === 'true');
          resolve();
        },
        onFailure: (err) => reject(err),
      });
    });
  }

  function signOut() {
    pool?.getCurrentUser()?.signOut();
    sessionStorage.removeItem('gg.idToken');
    setUser(null); setIdToken(null); setApproved(null);
    if (config) window.location.href = `${config.hostedUiDomain}/logout?client_id=${config.userPoolClientId}&logout_uri=${encodeURIComponent(window.location.origin)}`;
  }

  function oauthSignIn(provider: 'Google' | 'SignInWithApple') {
    if (!config) return;
    const redirectUri = `${window.location.origin}/auth/callback`;
    const url = `${config.hostedUiDomain}/oauth2/authorize?identity_provider=${provider}&response_type=code&client_id=${config.userPoolClientId}&scope=email+openid+profile&redirect_uri=${encodeURIComponent(redirectUri)}`;
    window.location.href = url;
  }

  async function refreshSession() {
    if (!pool) return;
    const current = pool.getCurrentUser();
    if (!current) return;
    return new Promise<void>((resolve) => {
      current.getSession((_: Error | null, session: CognitoUserSession | null) => {
        if (session) {
          setIdToken(session.getIdToken().getJwtToken());
          const payload = session.getIdToken().payload as Record<string, string>;
          setApproved(payload['custom:approved'] === 'true');
        }
        resolve();
      });
    });
  }

  return (
    <Ctx.Provider value={{ ready, config, user, idToken, approved, signUp, confirm, signIn, signOut, oauthSignIn, refreshSession }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error('AuthProvider missing');
  return c;
}
